/**
 * Создает экземпляр TableFilter для указанной таблицы.
 * Инициализирует обработчики событий для фильтрации и сортировки.
 *
 * @param {HTMLElement} table Элемент таблицы, к которому будет применяться фильтрация и сортировка.
 */
export class TableFilter {
  constructor(table) {
    this.table = table;
    this.initFilters();
  }

  /**
   * Инициализирует обработчики событий для элементов ввода и выбора в таблице.
   * Подключает события к текстовым полям и выпадающим спискам для фильтрации и к заголовкам колонок для сортировки.
   */
  initFilters() {
    // Обработка текстового ввода
    this.table.querySelectorAll('.table-search input').forEach((input) => {
      input.addEventListener('keyup', () => this.handleSearch());
    });

    // Обработка выбора в элементах <select>
    this.table.querySelectorAll('.table-search select').forEach((select) => {
      select.addEventListener('change', () => this.handleSearch());
    });

    this.table.querySelectorAll('.sortBy').forEach((header, idx) => {
      header.addEventListener('click', () => this.sortBy(idx, header));
    });
  }

  /**
   * Выполняет сортировку строк таблицы по указанной колонке.
   * Определяет тип данных для сортировки на основе атрибута заголовка колонки и применяет соответствующую логику сортировки.
   *
   * @param {number} n Индекс колонки, по которой будет выполнена сортировка.
   * @param {HTMLElement} header Элемент заголовка колонки, содержащий информацию о типе данных для сортировки.
   */
  sortBy(n, header) {
    const dataType = parseFloat(header.dataset.sorttype) ?? 0;
    const rowsArray = Array.from(this.table.querySelector('tbody').rows);

    const getCellValue = (row, index) => {
      const cellValue = row.getElementsByTagName('td')[index].innerHTML;
      switch (dataType) {
        case 1: // Число
          return parseFloat(cellValue.replace(/[^-\d.]/g, '')) || 0;
        case 2: // Дата
          const dateParts = cellValue.split('.'); // Разделяем строку на компоненты
          if (dateParts.length === 3) {
            const year = parseInt(dateParts[2], 10);
            const month = parseInt(dateParts[1], 10) - 1; // Месяцы начинаются с 0
            const day = parseInt(dateParts[0], 10);
            return new Date(year, month, day); // Создаем объект Date
          }
          return new Date(0);
        default: // Строка
          return cellValue.toLowerCase();
      }
    };

    rowsArray.sort((a, b) => {
      const aValue = getCellValue(a, n);
      const bValue = getCellValue(b, n);

      switch (dataType) {
        case 1: // Число
        case 2: // Дата
          return aValue - bValue;
        default: // Строка
          return aValue.localeCompare(bValue);
      }
    });

    // Определяем направление сортировки
    const currentDir = header.getAttribute('data-sort-dir');
    if (currentDir === 'asc') {
      rowsArray.reverse();
      header.setAttribute('data-sort-dir', 'desc');
    } else {
      header.setAttribute('data-sort-dir', 'asc');
    }

    // Перестройка таблицы с отсортированными строками
    rowsArray.forEach((row) => this.table.querySelector('tbody').appendChild(row));
  }

  /**
   * Выполняет фильтрацию строк таблицы на основе текстового ввода или выбранного значения в выпадающих списках.
   * Сбрасывает скрытие для всех строк перед применением новых фильтров.
   */
  handleSearch() {
    const rows = this.table.querySelector('tbody').querySelectorAll('tr:not(.table-search)');
    rows.forEach((row) => row.classList.remove('filter-hidden')); // Сбрасываем скрытие для всех строк

    // Обработка как элементов ввода, так и select
    this.table.querySelectorAll('.table-search input, .table-search select').forEach((input) => {
      this.filterRows(input, rows);
    });
  }

  /**
   * Фильтрует строки таблицы в соответствии с заданным критерием поиска.
   * Применяет различные стратегии сравнения в зависимости от типа поиска (текст, число, дата).
   *
   * @param {HTMLElement} input Элемент ввода или выбора, содержащий критерий фильтрации.
   * @param {NodeListOf<Element>} rows Коллекция строк таблицы для фильтрации.
   */
  filterRows(input, rows) {
    const key = input.dataset.key;
    let searchText = input.value.toLowerCase().trim();
    const typeSeatch = this.typeSeatch(searchText);

    rows.forEach((row) => {
      if (!row.classList.contains('filter-hidden')) {
        // Пропускаем уже скрытые строки
        const cell = row.getElementsByTagName('td')[key];
        if (cell) {
          this.compareValues(cell, searchText, typeSeatch, row);
        }
      }
    });
  }

  /**
   * Определяет тип поиска на основе содержимого строки поиска.
   * Возвращает числовой идентификатор типа поиска: текст, число или дата.
   *
   * @param {string} searchText Строка поиска, введенная пользователем.
   * @returns {number} Тип поиска: 0 - текст, 1 - число, 2 - дата.
   */
  typeSeatch(searchText) {
    const operator = searchText[0];
    const numericSearchPattern = searchText.substring(1).replace(/'/g, '').replace(/,/g, '.');
    const datePattern = /(\d{2})\.(\d{2})\.(\d{4})$/;

    if ((operator === '>' || operator === '<') && datePattern.test(searchText)) {
      return 2; // Дата
    } else if (!isNaN(parseFloat(numericSearchPattern)) && isFinite(numericSearchPattern) && (operator === '>' || operator === '<')) {
      return 1; // Число
    }
    return 0; // Обычный текст
  }

  /**
   * Сравнивает значения ячейки с заданным критерием поиска и скрывает строки, не соответствующие критериям.
   *
   * @param {HTMLElement} cell Ячейка таблицы для сравнения.
   * @param {string} searchText Строка поиска, введенная пользователем.
   * @param {number} typeSeatch Тип поиска (текст, число, дата).
   * @param {HTMLElement} row Строка таблицы для возможного скрытия.
   */
  compareValues(cell, searchText, typeSeatch, row) {
    let rowText = cell.innerHTML
      .toLowerCase()
      .replace(/\u200a/g, '')
      .replace(/'/g, '')
      .replace(/,/g, '.');

    let comparisonResult = false;
    switch (typeSeatch) {
      case 1:
        comparisonResult = this.performNumericComparison(searchText, rowText);
        break;
      case 2:
        comparisonResult = this.performDateComparison(searchText, rowText);
        break;

      default:
        comparisonResult = rowText.includes(searchText);
        break;
    }

    if (!comparisonResult) {
      row.classList.add('filter-hidden'); // Скрываем строку, если условие не совпадает
    }
  }

  performNumericComparison(searchText, rowText) {
    const operator = searchText[0];
    const searchNumber = parseFloat(searchText.substring(1).replace(/'/g, '').replace(/,/g, '.'));
    const rowNumber = parseFloat(rowText);
    if (operator === '>') {
      return rowNumber > searchNumber;
    } else if (operator === '<') {
      return rowNumber < searchNumber;
    }
    return false;
  }

  /**
   * Сравнивает значения ячейки с заданным критерием поиска и скрывает строки, не соответствующие критериям.
   * @param {string} searchText
   * @param {string} rowText
   * @returns
   */
  performDateComparison(searchText, rowText) {
    const operator = searchText[0];
    const searchDateText = searchText.substring(1).trim();
    const [searchDay, searchMonth, searchYear] = searchDateText.split('.').map((num) => parseInt(num, 10));
    const searchDate = new Date(searchYear, searchMonth - 1, searchDay);

    const [rowDay, rowMonth, rowYear] = rowText.split('.').map((num) => parseInt(num, 10));
    const rowDate = new Date(rowYear, rowMonth - 1, rowDay);

    switch (operator) {
      case '>':
        return rowDate > searchDate;
      case '<':
        return rowDate < searchDate;
      default:
        return false;
    }
  }
}
